import apiClient from './apiClient';
import { API_URL } from '../config';
import type { GradingData } from './transcriptionService';
import type { ReportStyleSpec } from './rxfService';

export type VertebralNumberingBasis = 'c2_downward' | 't12_lowest_rib' | 'l5_upward' | 'prior_correlation';

export interface CurrentStudyContext {
  current_modalities: Array<'CT' | 'MRI' | 'US' | 'PET' | 'NM' | 'X-ray' | 'DEXA' | 'Mammography' | 'Fluoroscopy'>;
}

export interface VertebralNumberingDeclaration {
  basis?: VertebralNumberingBasis;
  reference_details?: string;
  variant?: string;
  variant_description?: string;
  level_mapping: Record<string, string>;
  resolution_note?: string;
}

export interface VertebralNumberingIssue {
  code: string;
  severity: 'info' | 'warning' | 'blocking';
  message: string;
  affected_levels: string[];
  sections: string[];
  remediation: string;
}

export interface VertebralLevelReference {
  raw: string;
  normalized: string;
  kind: 'vertebra' | 'disc';
  first: string;
  second?: string | null;
  start: number;
  end: number;
  section: string;
}

export interface VertebralNumberingData extends VertebralNumberingDeclaration {
  schema_version: 1;
  active: boolean;
  basis_required: boolean;
  basis_allowed: boolean;
  detected_levels: string[];
  references: VertebralLevelReference[];
  issues: VertebralNumberingIssue[];
  validation_status: 'clear' | 'warning' | 'blocked';
  requires_radiologist_review: boolean;
  last_reviewed_by?: number | null;
  last_reviewed_at?: string | null;
  audit_history: Array<Record<string, unknown>>;
}

export interface VertebralNumberingConfig {
  enabled: boolean;
  enforcement_mode: 'risk_based' | 'warn' | 'off';
  allowed_bases: VertebralNumberingBasis[];
  required_basis_regions: string[];
  findings_wording: string;
  variant_impression_wording: string;
}

export interface ReportResponse {
  study_context?: CurrentStudyContext | null;
  id: number;
  session_id: string;
  template_id: string;
  report_text: string;
  diagnosis_category: string | null;
  pathology_type: string | null;
  cpt_code: string | null;
  cpt_exam_name: string | null;
  cpt_laterality: string | null;
  cpt_contrast: string | null;
  cpt_detection_method: string | null;
  cpt_detection_confidence: string | null;
  custom_template: string | null;
  reporting_profile_id?: number | null;
  reporting_profile_revision_id?: number | null;
  doctor_id: number | null;
  suggest_save_binding?: boolean;
  doctor_binding_id?: number | null;
  status: string;
  finalized_at: string | null;
  finalized_by: number | null;
  last_edited_at: string | null;
  last_edited_by: number | null;
  created_at: string;
  grading_data?: GradingData | null;
  vertebral_numbering_data?: VertebralNumberingData | null;
  generation_time_ms?: number | null;
}

export interface PatientContextPayload {
  first_name?: string;
  last_name?: string;
  mrn?: string;
  dob?: string;
  gender?: string;
  accession_number?: string;
  modality?: string;
  study_date?: string;
  body_part?: string;
}

export interface GenerateReportRequest {
  session_id: string;
  study_context?: CurrentStudyContext | null;
  clinical_history?: string;
  patient_context?: PatientContextPayload;
  cpt_code?: string;
  cpt_exam_name?: string;  // For disambiguating CPT code lookups
  cpt_detection_method?: string;  // "manual", "rule_based", "llm_detected", "fallback"
  cpt_modality?: string;  // e.g. "CT", "MRI" — from LLM detection metadata
  cpt_body_region?: string;  // e.g. "Abdomen" — from LLM detection metadata
  cpt_laterality?: string;  // e.g. "left", "right", "bilateral"
  cpt_contrast?: string;  // e.g. "without_contrast", "with_contrast"
  cpt_detection_confidence?: string;  // LLM confidence score as string
  custom_template?: string; // Custom template structure for report generation
  doctor_id?: number; // Referring doctor for doctor-specific template resolution
  vertebral_numbering?: VertebralNumberingDeclaration;
}

export interface UpdateReportRequest {
  sections: Record<string, string>;
  status?: string;
}

export interface FormatPreviewResponse {
  report_id: number;
  session_id: string;
  original_report_text: string;
  preview_report_text: string;
  style_spec: ReportStyleSpec;
  receipt: string;
  baseline_hash: string;
  preview_hash: string;
  pipeline_health: string;
}

const previewReportFormat = async (
  reportId: number,
  styleSpec: ReportStyleSpec,
): Promise<FormatPreviewResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/${reportId}/format-preview`, {
    style_spec: styleSpec,
  });
  return response.data;
};

const approveReportFormatPreview = async (
  reportId: number,
  styleSpec: ReportStyleSpec,
  previewReportText: string,
  receipt: string,
): Promise<ReportResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/${reportId}/format-preview/approve`, {
    style_spec: styleSpec,
    preview_report_text: previewReportText,
    receipt,
  });
  return response.data;
};

const generateReport = async (data: GenerateReportRequest): Promise<ReportResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/generate`, data);
  return response.data;
};

const getReport = async (sessionId: string): Promise<ReportResponse> => {
  const response = await apiClient.get(`${API_URL}/reports/${sessionId}`);
  return response.data;
};

const regenerateReport = async (data: GenerateReportRequest): Promise<ReportResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/regenerate`, data);
  return response.data;
};

const updateReport = async (reportId: number, data: UpdateReportRequest): Promise<ReportResponse> => {
  const response = await apiClient.put(`${API_URL}/reports/${reportId}`, data);
  return response.data;
};

const updateVertebralNumbering = async (
  reportId: number,
  data: VertebralNumberingDeclaration,
): Promise<ReportResponse> => {
  const response = await apiClient.put(`${API_URL}/reports/${reportId}/vertebral-numbering`, data);
  return response.data;
};

const finalizeReport = async (reportId: number): Promise<ReportResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/${reportId}/finalize`);
  return response.data;
};

const exportReportHTML = async (reportId: number, sessionId: string): Promise<void> => {
  const tzOffset = -new Date().getTimezoneOffset();
  const response = await apiClient.get(`${API_URL}/reports/${reportId}/export/html`, {
    params: { tz_offset: tzOffset },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'text/html' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `report_${reportId}_${sessionId}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const exportReportDOCX = async (reportId: number, sessionId: string): Promise<void> => {
  const tzOffset = -new Date().getTimezoneOffset();
  const response = await apiClient.get(`${API_URL}/reports/${reportId}/export/docx`, {
    params: { tz_offset: tzOffset },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `report_${reportId}_${sessionId}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const reportService = {
  generateReport,
  previewReportFormat,
  approveReportFormatPreview,
  getReport,
  regenerateReport,
  updateReport,
  updateVertebralNumbering,
  finalizeReport,
  exportReportHTML,
  exportReportDOCX,
};

export default reportService;
